'use client';

import React from 'react';

interface PrivacyPolicyPageProps {
  onNavigate: (page: string) => void;
}

const PrivacyPolicyPage: React.FC<PrivacyPolicyPageProps> = ({ onNavigate }) => {
  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '3rem 1.5rem 4rem' }}>
      {/* Back link */}
      <button
        onClick={() => onNavigate('home')}
        style={{
          background: 'none',
          border: 'none',
          color: '#f4c430',
          fontSize: '0.9rem',
          cursor: 'pointer',
          padding: 0,
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.4rem',
        }}
      >
        ← Back to Home
      </button>

      <h1
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
          color: '#f4c430',
          marginBottom: '0.5rem',
          fontWeight: 700,
        }}
      >
        Privacy Policy
      </h1>
      <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem', marginBottom: '2.5rem' }}>
        Last updated: October 2026
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        <Section title="1. Introduction">
          <p>
            Stellar Scholar Tutoring Services ("we," "us," or "our") is committed to protecting the privacy of our
            clients, students, and website visitors. This Privacy Policy explains how we collect, use, disclose, and
            safeguard your personal information in accordance with Canada's <em>Personal Information Protection and
            Electronic Documents Act</em> (PIPEDA) and applicable provincial privacy legislation.
          </p>
          <p style={{ marginTop: '0.75rem' }}>
            By using our website or services, you consent to the practices described in this policy.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <p>We may collect the following types of personal information:</p>
          <ul style={{ marginTop: '0.75rem', paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <li><strong style={{ color: 'rgba(255,255,255,0.9)' }}>Contact information</strong> — name, email address, and phone number provided when you sign up or contact us.</li>
            <li><strong style={{ color: 'rgba(255,255,255,0.9)' }}>Payment information</strong> — billing details processed securely through our payment provider (Stripe). We do not store full card numbers.</li>
            <li><strong style={{ color: 'rgba(255,255,255,0.9)' }}>Student information</strong> — grade level, subjects, and learning goals shared to facilitate tutoring sessions.</li>
            <li><strong style={{ color: 'rgba(255,255,255,0.9)' }}>Usage data</strong> — pages visited and interactions on our website, collected via analytics tools.</li>
          </ul>
        </Section>

        <Section title="3. How We Use Your Information">
          <p>We use your personal information to:</p>
          <ul style={{ marginTop: '0.75rem', paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <li>Schedule and deliver tutoring sessions</li>
            <li>Process payments and send order confirmations</li>
            <li>Respond to inquiries and provide customer support</li>
            <li>Send newsletters or updates (only with your consent)</li>
            <li>Improve our website and services</li>
          </ul>
          <p style={{ marginTop: '0.75rem' }}>
            We will not use your information for purposes other than those identified above without your consent, except
            as required or permitted by law.
          </p>
        </Section>

        <Section title="4. Sharing Your Information">
          <p>
            We do not sell, rent, or trade your personal information. We may share it only with:
          </p>
          <ul style={{ marginTop: '0.75rem', paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <li><strong style={{ color: 'rgba(255,255,255,0.9)' }}>Service providers</strong> — trusted third parties (e.g., payment processors, email platforms) who assist us in operating our business, bound by confidentiality obligations.</li>
            <li><strong style={{ color: 'rgba(255,255,255,0.9)' }}>Legal requirements</strong> — when required by law, court order, or government authority.</li>
          </ul>
        </Section>

        <Section title="5. Data Retention">
          <p>
            We retain personal information only as long as necessary to fulfill the purposes for which it was collected,
            or as required by law. When no longer needed, information is securely deleted or anonymized.
          </p>
        </Section>

        <Section title="6. Your Rights">
          <p>Under PIPEDA, you have the right to:</p>
          <ul style={{ marginTop: '0.75rem', paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <li>Access the personal information we hold about you</li>
            <li>Request corrections to inaccurate or incomplete information</li>
            <li>Withdraw consent for non-essential uses at any time</li>
            <li>Ask us to delete your information, subject to legal obligations</li>
          </ul>
          <p style={{ marginTop: '0.75rem' }}>
            To exercise any of these rights, please contact us at the address below.
          </p>
        </Section>

        <Section title="7. Cookies & Analytics">
          <p>
            Our website may use cookies and similar technologies to improve your browsing experience and analyze site
            traffic. You can disable cookies through your browser settings, though some features may not function as
            intended.
          </p>
        </Section>

        <Section title="8. Security">
          <p>
            We take reasonable technical and organizational measures to protect your personal information from
            unauthorized access, loss, or misuse. However, no method of transmission over the internet is completely
            secure, and we cannot guarantee absolute security.
          </p>
        </Section>

        <Section title="9. Contact Us">
          <p>
            If you have questions about this Privacy Policy or wish to exercise your rights, please contact us:
          </p>
          <div
            style={{
              marginTop: '1rem',
              padding: '1.25rem 1.5rem',
              background: 'rgba(244,196,48,0.06)',
              border: '1px solid rgba(244,196,48,0.15)',
              borderRadius: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: '0.4rem',
            }}
          >
            <p style={{ color: '#f4c430', fontWeight: 600, margin: 0 }}>Stellar Scholar Tutoring Services</p>
            <p style={{ margin: 0 }}>Canada</p>
            <p style={{ margin: 0 }}>
              Email:{' '}
              <a href="mailto:stellarscholartutoring@gmail.com" style={{ color: '#f4c430', textDecoration: 'none' }}>
                stellarscholartutoring@gmail.com
              </a>
            </p>
          </div>
        </Section>

        <Section title="10. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. The "Last updated" date at the top of this page
            reflects the most recent revision. Continued use of our services after changes are posted constitutes
            acceptance of the updated policy.
          </p>
        </Section>
      </div>
    </div>
  );
};

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, children }) => (
  <div>
    <h2
      style={{
        fontSize: '1.1rem',
        fontWeight: 600,
        color: 'rgba(255,255,255,0.95)',
        marginBottom: '0.75rem',
        paddingBottom: '0.5rem',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
      }}
    >
      {title}
    </h2>
    <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.92rem', lineHeight: 1.75 }}>
      {children}
    </div>
  </div>
);

export default PrivacyPolicyPage;
